import React, {useContext, useEffect, useState} from "react";
import FontAwesomeButton from "./cards/FontAwesomeButton";
import {faAngleDown, faAngleUp, faUser, faUsers, faUserTie} from "@fortawesome/free-solid-svg-icons";
import {ApiContext} from "../util/Requests";
import "./ManagementTree.scss";
import SimpleCardBox from "./cards/SimpleCardBox";

/**
 * @typedef {Array} ManagementLink
 */

/**
 * @typedef {Object} ManagementNode
 * @property {number} id - User ID
 * @property {string} name - User display name
 * @property {string} role - User role
 * @property {boolean} isExpanded - Whether the node is expanded
 * @property {ManagementNode[]} children - Child nodes
 * @property {number} managedCount - Number of users managed
 * @property {Object} _user - Original user data
 */

/**
 * ManagementTree component - Visualizes the company management hierarchy
 * @param {Object} props
 * @param {number} props.rootUserId - The ID of the root user to display tree for
 * @param {boolean} props.showAll - If true, shows all users in the organization
 * @param {ManagementLink[]} props.links - Array of management links [{manager_id, managed_id}]
 */
const ManagementTree = ({rootUserId, showAll = false, links = []}) => {
    const {caches, doRequest, loading, startLoading, stopLoading} = useContext(ApiContext);
    const [treeState, setTree] = useState([]);

    /**
     * Build a tree structure from flat user and management data
     */
    useEffect(() => {
        if (!caches.users.value.length) {
            setTree([]);
            return;
        }

        // Create a map of users by ID
        const userMap = new Map();
        caches.users.value.forEach(user => {
            userMap.set(user.insiteID, user);
        });

        // Build tree starting from root (or all users if showAll)
        const root = showAll ? null : rootUserId;

        const newTree = [];
        if (root && userMap.has(root)) {
            newTree.push(buildTreeRecursive(root, userMap, links, 0));
        } else if (showAll) {
            // If showing all, create a virtual root
            newTree.push(buildVirtualRoot(userMap, links, 0));
        }

        setTree(newTree);
    }, [caches.users.value, links, rootUserId, showAll]);

    /**
     * Recursively build tree nodes
     */
    function buildTreeRecursive(userId, userMap, links) {
        /**
         * @type {SC_User}
         */
        const user = userMap.get(userId);
        if (!user) return null;

        // Get children (users managed by this user)
        let children = links
            .filter(link => link[0] === userId)
            .map(link => link[1])
            .map(childId => buildTreeRecursive(childId, userMap, links))
            .filter(Boolean);

        // Sort children by managedCount (descending) - users with most direct children first
        children.sort((a, b) => b.managedCount - a.managedCount);

        return {
            id: user.paycomID,
            name: user.name,
            role: user.paycomID ? "Employee" : "Sub",
            isExpanded: true,
            children,
            managedCount: children.length,
            _user: user,
        };
    }

    /**
     * Create a virtual root node for showing all users
     */
    function buildVirtualRoot(userMap, links) {
        const children = Array.from(userMap.values())
            .map(user => {
                const managedIds = links
                    .filter(link => link[0] === user.id)
                    .map(link => link[1]);

                return {
                    id: user.id,
                    name: user.name || user.display_name,
                    role: user.role || "Employee",
                    isExpanded: true,
                    children: managedIds
                        .map(childId => buildTreeRecursive(childId, userMap, links))
                        .filter(Boolean),
                    managedCount: managedIds.length,
                    _user: user,
                };
            });

        // Sort children by managedCount (descending) - users with most direct children first
        children.sort((a, b) => b.managedCount - a.managedCount);

        return {
            id: "__root__",
            name: "All Users",
            role: "Root",
            isExpanded: true,
            children,
            managedCount: children.length,
            _user: null,
        };
    }

    /**
     * Recursively find and update a node's isExpanded state
     */
    function updateNodeExpanded(nodes, nodeId, newValue) {
        return nodes.map(node => {
            if (node.id === nodeId) {
                return { ...node, isExpanded: newValue };
            }
            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: updateNodeExpanded(node.children, nodeId, newValue)
                };
            }
            return node;
        });
    }

    /**
     * Toggle node expansion
     */
    const toggleExpand = (node) => {
        const newExpandedState = !node.isExpanded;
        const updatedTree = updateNodeExpanded(treeState, node.id, newExpandedState);
        setTree(updatedTree);
    };

    /**
     * Get user role badge color
     */
    const getRoleColor = (role) => {
        console.log(role);
        const roleLower = role?.toLowerCase() || "";
        if (roleLower.includes("admin")) return "#e74c3c";
        if (roleLower.includes("manager")) return "#3498db";
        if (roleLower.includes("lead")) return "#2ecc71";
        return "#95a5a6";
    };

    if (!treeState.length) {
        return <div className="management-tree-loading">Loading management structure...</div>;
    }

    return (
        <SimpleCardBox defaultPadding={true} defaultMargin={false}>
            <h4 className="management-tree-title">Management Structure</h4>
            {treeState.map(node => (
                <ManagementTreeNode
                    key={node.id}
                    node={node}
                    onToggle={toggleExpand}
                    getRoleColor={getRoleColor}
                />
            ))}
        </SimpleCardBox>
    );
};

/**
 * Individual tree node component
 */
const ManagementTreeNode = ({node, onToggle, getRoleColor}) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = node.isExpanded;

    // Add indentation class based on depth
    const indentationClass = `management-tree-indent`;

    if (!hasChildren) {
        // Leaf node - just display the user
        return (
            <div className={indentationClass}>
                <div className="management-tree-node management-tree-leaf-node">
                    <div className="management-tree-leaf-content">
                        <FontAwesomeButton icon={faUser} extraClasses={node.id ? "management-tree-icon" : "management-tree-sub-icon"}/>
                        <div className="management-tree-leaf-info">
                            <div className="management-tree-leaf-name">
                                <strong>{node.name}{node.id ? ` (${node.id})` : null}</strong>
                                <span className="management-tree-role-badge" style={{backgroundColor: getRoleColor(node.role)}}>
                                    {node.role}
                                </span>
                                {node.managedCount > 0 && (
                                    <span className="management-tree-managed-count">
                                    Manages {node.managedCount} user{node.managedCount !== 1 ? "s" : ""}
                                </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Branch node - has children
    return (
        <div className={indentationClass}>
            <div className="management-tree-branch-header" onClick={() => onToggle(node)}>
                <div className="management-tree-branch-icon">
                    {isExpanded ? <FontAwesomeButton icon={faAngleUp} extraClasses="management-tree-expand-icon"/> : <FontAwesomeButton icon={faAngleDown} extraClasses="management-tree-expand-icon"/>}
                </div>
                <div className="management-tree-branch-content">
                    <div className="management-tree-branch-name">
                        <FontAwesomeButton icon={faUserTie} extraClasses="management-tree-manager-icon"/>
                        <strong>{node.name} ({node.id})</strong>
                        <span className="management-tree-role-badge" style={{backgroundColor: getRoleColor(node.role)}}>
                            {node.role}
                        </span>
                        {node.managedCount > 0 && (
                            <div className="management-tree-managed-count">
                                <FontAwesomeButton icon={faUsers} extraClasses="management-tree-count-icon"/>
                                {node.managedCount} user{node.managedCount !== 1 ? "s" : ""}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {isExpanded && (
                <div className="management-tree-children-container">
                    {node.children.map(child => (
                        <ManagementTreeNode
                            key={child.id}
                            node={child}
                            onToggle={onToggle}
                            getRoleColor={getRoleColor}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ManagementTree;
